
import { CalendarClock, Clock, MapPin, Users, Mountain, ExternalLink, Info } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Ride } from '@/lib/types/core/ride';
import { formatDate, formatTime } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

interface RidePreviewCardProps {
  ride: Ride;
}

const RidePreviewCard = ({ ride }: RidePreviewCardProps) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-500';
      case 'Moderate':
        return 'bg-blue-500';
      case 'Hard':
        return 'bg-adv-orange';
      default:
        return 'bg-gray-500';
    }
  };
  
  return (
    <div className="glass-card rounded-lg overflow-hidden shadow-lg animate-fade-in">
      <div className="relative">
        {ride.image_url ? (
          <div className="aspect-[3/2] overflow-hidden">
            <img 
              src={ride.image_url} 
              alt={ride.title} 
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[3/2] bg-adv-darkGray flex items-center justify-center">
            <Mountain size={48} className="text-adv-mediumGray" />
          </div>
        )}
        
        <div className="absolute top-2 right-2">
          <div className={`text-xs font-medium px-2.5 py-1 rounded-full text-white ${getDifficultyColor(ride.terrain_difficulty)}`}>
            {ride.terrain_difficulty}
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="font-bold text-white text-lg mb-3">
          {ride.title}
        </h3>
        
        <div className="space-y-2 mb-3">
          <div className="flex items-center text-sm text-adv-lightGray">
            <CalendarClock size={16} className="mr-2 text-adv-orange" />
            {ride.multi_day && ride.end_date
              ? `${formatDate(ride.meeting_date, false)} - ${formatDate(ride.end_date, false)}`
              : formatDate(ride.meeting_date, false)
            }
            {ride.multi_day && !ride.end_date && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="ml-2 text-adv-orange cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This is a multi-day ride</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center text-sm text-adv-lightGray">
            <Clock size={16} className="mr-2 text-adv-orange" /> 
            Kickstands up: {formatTime(ride.kickstands_up)}
          </div>
          <div className="flex items-center text-sm text-adv-lightGray">
            <MapPin size={16} className="mr-2 text-adv-orange" /> 
            Meeting point
          </div>
          <div className="flex items-center text-sm text-adv-lightGray">
            <Users size={16} className="mr-2 text-adv-orange" /> 
            {ride.rider_skill} riders
          </div>
        </div>
        
        <p className="text-adv-lightGray text-sm mb-4 line-clamp-2">
          {ride.description}
        </p>
        
        <div className="flex justify-between items-center">
          {ride.gpx_link && (
            <a 
              href={ride.gpx_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-adv-orange hover:text-adv-orange/80 text-sm flex items-center"
            >
              <ExternalLink size={14} className="mr-1" /> Route
            </a>
          )}
          
          <NavLink 
            to={`/explore-rides/${ride.id}`}
            className="text-adv-orange hover:text-adv-orange/80 text-sm flex items-center ml-auto"
          >
            View Details
            <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default RidePreviewCard;
